<template>
  <PopupModal :open="open" @close="close">
    <template #header>
      <p class="textHeadline3">
        {{ t("more.change.password.title") }}
      </p>
    </template>
    <template #body>
      <p class="textBody1 mb-5" v-html="t('more.change.password.text')"></p>
      <PasswordElement
        id="currentPassword"
        v-model="currentPassword"
        type="password"
        :label="t('more.change.password.currentpassword.label')"
        @update-field="(value) => (currentPassword = value)"
      />
      <PasswordElement
        id="newPassword"
        v-model="newPassword"
        type="password"
        :label="t('more.change.password.newpassword.label')"
        @update-field="(value) => (newPassword = value)"
      />
      <PasswordValidationInfo
        :password="newPassword"
        @is-password-valid="setIsNewPasswordValid"
      />
      <PasswordElement
        id="newPasswordRepeated"
        v-model="newPasswordRepeated"
        type="password"
        :label="t('more.change.password.newpasswordrepeated.label')"
        @update-field="(value) => (newPasswordRepeated = value)"
      />
    </template>
    <template #footer>
      <div class="flex flex-row gap-2 flex-wrap md:flex-nowrap">
        <ButtonElement
          :label="t('more.change.password.cancel')"
          class="w-full"
          :disabled-visible="true"
          @click="close"
        />
        <ButtonElement
          :disabled="isSubmitButtonDisabled()"
          :label="t('more.change.password.submit')"
          class="w-full"
          @click="onSubmit"
        />
      </div>
    </template>
  </PopupModal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import PopupModal from "../layout/PopupModal.vue";
import { useI18n } from "vue-i18n";
import PasswordElement from "~/components/v2/form/PasswordElement.vue";
import PasswordValidationInfo from "~/components/v2/login/PasswordValidationInfo.vue";
import ButtonElement from "~/components/v2/form/ButtonElement.vue";
import { changePassword } from "~/api/v2/user.ts";
import { useNotificationStore } from "~/store/v2/notification";

defineProps<{
  open: boolean;
}>();

const { t } = useI18n();
const notificationStore = useNotificationStore();
const emit = defineEmits(["close"]);
const currentPassword = ref<string>("");
const newPassword = ref<string>("");
const newPasswordRepeated = ref<string>("");
const isNewPasswordValid = ref<boolean>(false);

function setIsNewPasswordValid(valid: boolean) {
  isNewPasswordValid.value = valid;
}

function close() {
  isNewPasswordValid.value = false;
  emit("close");
}

async function onSubmit() {
  try {
    const res = await changePassword(
      currentPassword.value,
      newPassword.value,
      newPasswordRepeated.value
    );

    if (res.status === 200) {
      notificationStore.addNotification(
        Math.random(),
        null,
        t("more.change.password.success.title"),
        t("more.change.password.success.text"),
        false
      );

      close();
    }
  } catch (error) {
    if (error.response.status === 412) {
      notificationStore.addNotification(
        Math.random(),
        null,
        t("web.general.error.message.title"),
        t("web.general.error.message.text")
      );
    }
  }
}

function isSubmitButtonDisabled() {
  return (
    !isNewPasswordValid.value ||
    !currentPassword.value ||
    newPassword.value !== newPasswordRepeated.value
  );
}
</script>
